import { useCallback } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { type DragEndEvent } from '@dnd-kit/core';
import { shuffle } from 'es-toolkit';
import { nanoid } from 'nanoid';

import { focusField } from '../../focus/focusUtils';
import { type BuildFormControl } from '../../type';
import { useQuizControl } from '../QuizControlProvider';

export const useMultipleOptions = ({
  control,
  index,
}: {
  control: BuildFormControl;
  index: number;
}) => {
  const { index: quizIndex } = useQuizControl();
  const { fields, append, remove, insert, move, replace } = useFieldArray({
    control,
    name: `quizList.${index}.multipleData.choiceList`,
  });

  const choiceList = useWatch({
    control,
    name: `quizList.${index}.multipleData.choiceList`,
  });

  const handleAdd = useCallback(() => {
    append({ key: nanoid(8), text: '', richText: '', isAnswer: false });
  }, [append]);

  const handleInsert = useCallback(
    (index: number) => {
      if (fields.length >= 5) return;
      insert(index, {
        key: nanoid(8),
        text: '',
        richText: '',
        isAnswer: false,
      });
      focusField(
        `quizList.${quizIndex}.multipleData.choiceList.${index}.text`,
        {
          scroll: true,
          delay: 100,
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insert, quizIndex],
  );

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  const handleMove = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const oldIndex = fields.findIndex(o => o.key === active.id);
        const newIndex = fields.findIndex(o => o.key === over?.id);

        move(oldIndex, newIndex);
      }
    },
    [fields, move],
  );

  const handleShuffle = useCallback(() => {
    const shuffledChoiceList = shuffle(choiceList);
    replace(shuffledChoiceList);
  }, [choiceList, replace]);

  return {
    handleAdd,
    handleRemove,
    handleMove,
    handleInsert,
    handleShuffle,
    options: fields,
  };
};
